import React, { useState, useRef, useEffect, useContext } from 'react';
import { DynamicIcon } from './icons.jsx';
import { ThemeContext } from '../context/ThemeContext.jsx';
import { useNavigate } from 'react-router-dom';
import { useData } from '../hooks/useData.js';
import { Snackbar, Alert } from '@mui/material';
import { Avatar } from './Common.jsx';
import { apiFetch } from '../api';

const getRoleBadgeStyles = (role) => {
    const r = (role || '').toLowerCase();
    if (r === 'r1' || r.includes('super admin')) {
        return 'bg-violet-50 text-violet-750 border-violet-200/60 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800/50';
    }
    if (r === 'r2' || r.includes('platform admin')) {
        return 'bg-indigo-50 text-indigo-750 border-indigo-200/60 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50';
    }
    if (r === 'r3' || r.includes('partner admin')) {
        return 'bg-sky-50/70 text-sky-700 border-sky-200/60 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800/50';
    }
    if (r === 'r4' || r.includes('tenant admin')) {
        return 'bg-emerald-50 text-emerald-750 border-emerald-200/60 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50';
    }
    if (r === 'r5' || r.includes('support admin')) {
        return 'bg-amber-50 text-amber-750 border-amber-200/60 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50';
    }
    if (r === 'r6' || r.includes('app creator')) {
        return 'bg-pink-50 text-pink-750 border-pink-200/60 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800/50';
    }
    if (r === 'r7' || r.includes('tenant user')) {
        return 'bg-teal-50 text-teal-750 border-teal-200/60 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800/50';
    }
    if (r === 'r8' || r === 'r9' || r === 'r10' || r.includes('agent')) {
        return 'bg-orange-50 text-orange-750 border-orange-200/60 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800/50';
    }
    return 'bg-gray-50 text-gray-750 border-gray-200/60 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700/50';
};

// Sleek helper for circular remaining percentage progress
const CircularProgress = ({ percentage, isUnlimited }) => {
    const RADIUS = 15;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS; // ≈ 94.25

    if (isUnlimited) {
        return (
            <div className="relative flex items-center justify-center w-9 h-9 shrink-0">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    {/* Track — theme-aware via CSS var opacity */}
                    <circle
                        cx="18" cy="18" r={RADIUS}
                        fill="none"
                        stroke="rgb(var(--primary-rgb) / 0.12)"
                        strokeWidth="3"
                    />
                    {/* Full arc in tenant primary color */}
                    <circle
                        cx="18" cy="18" r={RADIUS}
                        fill="none"
                        stroke="var(--primary-color)"
                        strokeWidth="3"
                        strokeDasharray={`${CIRCUMFERENCE} ${CIRCUMFERENCE}`}
                        strokeLinecap="round"
                    />
                </svg>
                <span className="absolute text-[11px] font-bold leading-none" style={{ color: 'var(--primary-color)' }}>
                    ∞
                </span>
            </div>
        );
    }

    const pct = Math.min(Math.max(percentage || 0, 0), 100);
    const filledLength = (pct / 100) * CIRCUMFERENCE;
    const strokeDasharray = `${filledLength} ${CIRCUMFERENCE}`;

    // Healthy ≥40%: tenant primary; warning <40%: amber; critical <10%: red
    let arcColor, labelColor;
    if (pct < 10) {
        arcColor = '#ef4444';   // red-500
        labelColor = '#ef4444';
    } else if (pct < 40) {
        arcColor = '#f59e0b';   // amber-500
        labelColor = '#f59e0b';
    } else {
        arcColor = 'var(--primary-color)';
        labelColor = 'var(--primary-color)';
    }

    const label = Math.round(pct);

    return (
        <div className="relative flex items-center justify-center w-9 h-9 shrink-0">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                {/* Track — subtle tenant-tinted background ring */}
                <circle
                    cx="18" cy="18" r={RADIUS}
                    fill="none"
                    stroke="rgb(var(--primary-rgb) / 0.12)"
                    strokeWidth="3"
                />
                {/* Progress arc */}
                <circle
                    cx="18" cy="18" r={RADIUS}
                    fill="none"
                    stroke={arcColor}
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 0.5s ease' }}
                />
            </svg>
            {/* Label */}
            <span
                className="absolute text-[9px] font-extrabold leading-none tracking-tight"
                style={{ color: labelColor }}
            >
                {label < 100 ? `${label}%` : '✓'}
            </span>
        </div>
    );
};




const UserProfileMenu = ({ 
    user, 
    onLogout, 
    token, 
    onProfileClick, 
    onRestartTour, 
    onDocsClick, 
    onSupportClick, 
    isSidebarMode = false, 
    isTenantUser = false, 
    triggerVariant = 'avatar' 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { theme, setTheme } = useContext(ThemeContext);
    const navigate = useNavigate();

    const getRoleDisplayName = () => {
        if (user?.role) return user.role;
        if (!user?.roleId) return '';
        const roleMap = {
            'r1': 'Super Admin',
            'r2': 'Platform Admin',
            'r3': 'Partner Admin',
            'r4': 'Tenant Admin',
            'r5': 'Support Admin',
            'r6': 'App Creator',
            'r7': 'Tenant User',
            'r8': 'Support Agent',
            'r9': 'Support Agent Senior',
            'r10': 'Support Agent Expert'
        };
        return roleMap[user.roleId] || user.roleId;
    };
    const roleDisplayName = getRoleDisplayName();

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const [wallet, setWallet] = useState(null);
    const isPartnerAdmin = user?.roleScope === 'Partner' || user?.roleId === 'r3';

    useEffect(() => {
        if (!token) {
            setWallet(null);
            return;
        }

        if (!isTenantUser && !isPartnerAdmin) {
            setWallet(null);
            return;
        }

        const fetchWallet = async () => {
            try {
                if (isTenantUser) {
                    const data = await apiFetch('/credits/wallet', token);
                    if (data) {
                        setWallet(data);
                    }
                } else if (isPartnerAdmin) {
                    const data = await apiFetch('/partners/me/credits', token);
                    if (data) {
                        setWallet({
                            monthly_allocation: data.pool_total,
                            available: data.pool_available,
                            pool_total: data.pool_total,
                            pool_available: data.pool_available,
                            pool_allocated: data.pool_allocated
                        });
                    }
                }
            } catch (err) {
                console.error('[UserProfileMenu] Failed to fetch wallet/credits info:', err);
            }
        };

        fetchWallet();
        const interval = setInterval(fetchWallet, 10000);
        return () => clearInterval(interval);
    }, [token, isTenantUser, isPartnerAdmin]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleThemeChange = async (newMode) => {
        setTheme(prev => ({ ...prev, mode: newMode }));
        if (token) {
            try {
                await apiFetch('/users/me/theme', token, {
                    method: 'PUT',
                    body: JSON.stringify({ mode: newMode })
                });
            } catch (err) {
                console.error('Failed to persist theme mode:', err);
            }
        }
    };

    // Position: side-panel footer dropdown opens upward and to the right/left
    const dropdownPositionClass = isSidebarMode
        ? 'bottom-full mb-2 left-0'
        : 'right-0 mt-3';

    return (
        <div className="relative text-left" ref={dropdownRef}>
            {/* Trigger Button Variants */}
            {triggerVariant === 'vercel-footer' ? (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex items-center justify-start gap-2.5 w-full min-w-0 rounded-lg p-1.5 hover:bg-black/5 dark:hover:bg-white/5 transition-all text-left group ${isOpen ? 'bg-black/5 dark:bg-white/5' : ''}`}
                >
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span className="shrink-0">
                            <Avatar 
                                src={user?.profile_pic ? (user.profile_pic.startsWith('data:') ? user.profile_pic : `data:image/png;base64,${user.profile_pic}`) : null}
                                name={user?.name || 'User'}
                                size={30}
                            />
                        </span>
                        <span className="flex flex-col min-w-0 text-left">
                            <span className="text-[13px] font-semibold truncate leading-tight text-gray-700 dark:text-gray-200">
                                {user?.name || 'User'}
                            </span>
                            {roleDisplayName && (
                                <span 
                                    className={`inline-flex items-center justify-center text-[9px] font-extrabold px-1.5 py-[2px] rounded tracking-wide border w-fit leading-normal mt-1 shrink-0 ${getRoleBadgeStyles(roleDisplayName || user?.roleId)}`}
                                >
                                    {roleDisplayName}
                                </span>
                            )}
                        </span>
                    </div>
                </button>
            ) : triggerVariant === 'dots' ? (
                <button
                    id="user-profile-menu-dots"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${isOpen ? 'bg-black/5 dark:bg-white/5 text-gray-700 dark:text-gray-200' : 'text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    title="More options"
                >
                    <DynamicIcon name="MoreHorizontal" className="w-4 h-4" />
                </button>
            ) : (
                <button
                    id="user-profile-menu"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex items-center p-0.5 rounded-full transition-all duration-300 border-2 ${isOpen ? 'border-primary-500 ring-4 ring-primary-500/10' : 'border-transparent hover:border-gray-200'}`}
                    title="Account"
                >
                    <Avatar 
                        src={user?.profile_pic ? (user.profile_pic.startsWith('data:') ? user.profile_pic : `data:image/png;base64,${user.profile_pic}`) : null}
                        name={user?.name || 'User'}
                        size={32}
                    />
                </button>
            )}

            {/* Dropdown Card */}
            {isOpen && (
                <div 
                    className={`absolute ${dropdownPositionClass} w-[280px] bg-white dark:bg-gray-900 rounded-xl shadow-[0_12px_30px_rgba(0,0,0,0.15)] dark:shadow-[0_12px_30px_rgba(0,0,0,0.4)] border border-gray-200/60 dark:border-gray-800 overflow-hidden z-[300] transition-all`}
                >
                    {/* Top Identity Block */}
                    <div 
                        onClick={() => {
                            setIsOpen(false);
                            if (onProfileClick) onProfileClick();
                            else navigate('/profile');
                        }}
                        className="flex items-center justify-between p-3.5 border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-all group/identity duration-200"
                        title="Profile Settings"
                    >
                        <div className="flex flex-col min-w-0 pr-2">
                            <span className="text-[13px] font-semibold text-gray-900 dark:text-white leading-tight truncate group-hover/identity:text-primary-600 dark:group-hover/identity:text-primary-400 transition-colors duration-200">
                                {user?.name || 'User'}
                            </span>
                            {roleDisplayName && (
                                <div className="mt-1 flex">
                                    <span 
                                        className={`inline-flex items-center justify-center px-1.5 py-[2px] rounded text-[9.5px] font-bold tracking-wide border shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all duration-200 leading-normal ${getRoleBadgeStyles(roleDisplayName || user?.roleId)}`}
                                    >
                                        {roleDisplayName}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div
                            className="p-1.5 rounded-md text-gray-400 group-hover/identity:text-gray-700 dark:group-hover/identity:text-gray-200 group-hover/identity:bg-black/5 dark:group-hover/identity:bg-white/5 transition-all duration-200 shrink-0"
                        >
                            <DynamicIcon name="Settings" className="w-4 h-4" />
                        </div>
                    </div>

                    {/* Credit Balance Section — hidden for Super Admin (r1) and Platform Admin (r2) */}
                    {user?.roleId !== 'r1' && user?.roleId !== 'r2' && (
                    <div className="mx-2.5 my-2 p-3 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl border border-gray-100 dark:border-gray-800/60 shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)]">
                        {/* Header Row */}
                        <div className="flex items-center justify-between mb-2.5">
                            <div className="flex items-center gap-2">
                                <DynamicIcon name="Coins" className="w-4 h-4 text-amber-500" />
                                <span className="text-[12px] font-semibold text-gray-700 dark:text-gray-200">Balance</span>
                            </div>
                            <CircularProgress 
                                percentage={(isTenantUser || isPartnerAdmin) ? (wallet?.monthly_allocation > 0 ? (wallet?.available / wallet?.monthly_allocation) * 100 : 0) : 100} 
                                isUnlimited={!isTenantUser && !isPartnerAdmin} 
                            />
                        </div>

                        {/* Details Rows */}
                        <div className="space-y-1.5 text-[12px]">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-400 dark:text-gray-500 font-medium">
                                    {isPartnerAdmin ? 'Total Pool' : 'Total'}
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {isTenantUser || isPartnerAdmin 
                                        ? `${(wallet?.monthly_allocation || 0).toLocaleString()} credits` 
                                        : 'Unlimited'}
                                </span>
                            </div>
                            {isPartnerAdmin && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400 dark:text-gray-500 font-medium">Allocated</span>
                                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                                        {`${(wallet?.pool_allocated || 0).toLocaleString()} credits`}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <span className="text-gray-400 dark:text-gray-500 font-medium">
                                    {isPartnerAdmin ? 'Available' : 'Remaining'}
                                </span>
                                <span className={`font-semibold ${
                                    (isTenantUser || isPartnerAdmin) && (wallet?.monthly_allocation > 0 ? (wallet?.available / wallet?.monthly_allocation) * 100 : 0) < 10 
                                        ? 'text-red-500 dark:text-red-400 animate-pulse' 
                                        : (isTenantUser || isPartnerAdmin) && (wallet?.monthly_allocation > 0 ? (wallet?.available / wallet?.monthly_allocation) * 100 : 0) < 40 
                                        ? 'text-amber-500 dark:text-amber-400' 
                                        : 'text-gray-900 dark:text-white'
                                }`}>
                                    {isTenantUser || isPartnerAdmin ? (wallet?.available || 0).toLocaleString() : 'Unlimited'}
                                </span>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Navigation Links with labels on left and icons on right */}
                    <div className="p-1 space-y-0.5">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (onSupportClick) onSupportClick();
                                else navigate('/support');
                            }}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 transition-all text-left group"
                        >
                            <span className="text-[13px] font-medium">Support</span>
                            <DynamicIcon name="LifeBuoy" className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                        </button>

                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (onDocsClick) onDocsClick();
                                else window.open(isTenantUser ? '/tenant-guide' : '/user-guide', '_blank');
                            }}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 transition-all text-left group"
                        >
                            <span className="text-[13px] font-medium">Docs</span>
                            <DynamicIcon name="BookOpen" className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                        </button>

                        {onRestartTour && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onRestartTour();
                                }}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 transition-all text-left group"
                            >
                                <span className="text-[13px] font-medium">Quick Tour</span>
                                <DynamicIcon name="Sparkles" className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                            </button>
                        )}

                        {/* Theme Toggle row: label left, control right */}
                        <div className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 transition-all">
                            <span className="text-[13px] font-medium">Theme</span>
                            <div className="flex bg-gray-100/80 dark:bg-gray-800 p-0.5 rounded-lg border border-gray-200/40 dark:border-gray-700/60">
                                {[
                                    { mode: 'system', icon: 'Monitor' },
                                    { mode: 'light', icon: 'Sun' },
                                    { mode: 'dark', icon: 'Moon' }
                                ].map(({ mode, icon }) => {
                                    const isActive = theme.mode === mode;
                                    return (
                                        <button
                                            key={mode}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleThemeChange(mode);
                                            }}
                                            className={`p-1.5 rounded-md transition-all ${
                                                isActive
                                                    ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                                            }`}
                                            title={`${mode.charAt(0).toUpperCase() + mode.slice(1)} Mode`}
                                        >
                                            <DynamicIcon name={icon} className="w-4 h-4" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="h-[1px] bg-gray-100 dark:bg-gray-800 my-1"></div>

                    {/* Log Out */}
                    <div className="p-1">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                onLogout();
                            }}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all text-left font-medium group"
                        >
                            <span className="text-[13px]">Log Out</span>
                            <DynamicIcon name="LogOut" className="w-4 h-4 text-red-400 group-hover:text-red-500" />
                        </button>
                    </div>
                </div>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserProfileMenu;
